// NEW_FILE_JUSTIFIED: Policy application field dispatcher — renders correct input component per TFormFieldType; distinct from DynamicFieldInput which handles old fieldValues system
import { format, parse } from 'date-fns';
import { CalendarIcon, Mail, Phone } from 'lucide-react';
import { cn } from '@/lib/utils';
import { isFieldConditionallyVisible } from '@/lib/formFieldVisibility';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { AddressField } from './AddressField';
import { RepeatableGroupField } from './RepeatableGroupField';
import { ConditionalDetailField } from './ConditionalDetailField';
import { usePresetOptions } from '@/hooks/usePresetOptions';
import type {
  IFieldRendererProps,
  IAddressValue,
  IConditionalDetailValue,
  IRepeatableGroupEntry,
  IFormField,
} from '@/@types/policyFormTemplates';
import type { TPresetKey } from '@/@types/insuranceBuilder';

const EMPTY_ADDRESS: IAddressValue = { street: '', city: '', postalCode: '', country: '' };

// Hook: resolves select/multiSelect options from either custom strings or a preset source.
// Must be called unconditionally at the top of FieldRenderer — never inside the switch.
function useResolvedOptionsForField(
  field: IFormField,
  allValues: Record<string, unknown>
): { label: string; value: string }[] {
  const source = field.optionsSource ?? 'custom';

  const parentValue =
    source === 'preset:carModels' && field.dependsOnFieldKey
      ? String(allValues[field.dependsOnFieldKey] ?? '')
      : undefined;

  const presetKey: TPresetKey =
    source === 'preset:countries'
      ? 'countries'
      : source === 'preset:occupations'
        ? 'occupations'
        : source === 'preset:carBrands'
          ? 'carBrands'
          : source === 'preset:carModels'
            ? 'carModels'
            : 'countries';

  const { options: presetOptions } = usePresetOptions({ key: presetKey, parentValue });

  if (source === 'custom') {
    return (field.customOptions ?? []).map(o => ({ label: o, value: o }));
  }
  return presetOptions.map(o => ({ label: o.label, value: o.value }));
}

export function FieldRenderer({
  field,
  value,
  onChange,
  allValues,
  disabled,
}: IFieldRendererProps) {
  // Unconditional hook call — rules-of-hooks compliant (called before any guard returns)
  const resolvedOptions = useResolvedOptionsForField(field, allValues);

  if (!isFieldConditionallyVisible(field, allValues)) return null;

  const handleChange = (val: unknown) => onChange(field.key, val);

  // Typed coercions — value is unknown because form values are heterogeneous;
  // each case only runs when fieldType guarantees the correct runtime type.
  const asStr = (value as string) || ''; // AS_JUSTIFIED: value is unknown; fieldType switch guarantees string here
  const asNum = (value as number) ?? ''; // AS_JUSTIFIED: value is unknown; fieldType switch guarantees number here
  const asBool = (value as boolean) || false; // AS_JUSTIFIED: value is unknown; fieldType switch guarantees boolean here
  const asAddress = (value as IAddressValue) || EMPTY_ADDRESS; // AS_JUSTIFIED: value is unknown; fieldType switch guarantees IAddressValue here
  const asEntries = (value as IRepeatableGroupEntry[]) || []; // AS_JUSTIFIED: value is unknown; fieldType switch guarantees IRepeatableGroupEntry[] here
  const asConditional = (value as IConditionalDetailValue) || { answer: false }; // AS_JUSTIFIED: value is unknown; fieldType switch guarantees IConditionalDetailValue here
  const asStrArray = (value as string[]) || []; // AS_JUSTIFIED: value is unknown; fieldType switch guarantees string[] here

  switch (field.fieldType) {
    case 'text':
    case 'email':
    case 'phone': {
      const hasIcon = field.fieldType === 'email' || field.fieldType === 'phone';
      return (
        <div className="space-y-2">
          <Label>
            {field.label}
            {field.required && <span className="text-destructive ml-1">*</span>}
          </Label>
          <div className={hasIcon ? 'relative' : undefined}>
            {field.fieldType === 'email' && (
              <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-zinc-500 pointer-events-none" />
            )}
            {field.fieldType === 'phone' && (
              <Phone className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-zinc-500 pointer-events-none" />
            )}
            <Input
              type={
                field.fieldType === 'email' ? 'email' : field.fieldType === 'phone' ? 'tel' : 'text'
              }
              value={asStr}
              onChange={e => handleChange(e.target.value)}
              placeholder={field.placeholder}
              disabled={disabled}
              data-testid={`field-${field.key}`}
              className={hasIcon ? 'pl-9' : undefined}
            />
          </div>
          {field.helpText && <p className="text-xs text-muted-foreground">{field.helpText}</p>}
          {field.errorMessage && <p className="text-xs text-destructive">{field.errorMessage}</p>}
        </div>
      );
    }

    case 'postalCode':
      return (
        <div className="space-y-2">
          <Label>
            {field.label}
            {field.required && <span className="text-destructive ml-1">*</span>}
          </Label>
          <Input
            type="text"
            inputMode="numeric"
            value={asStr}
            onChange={e => handleChange(e.target.value)}
            placeholder={field.placeholder}
            disabled={disabled}
            data-testid={`field-${field.key}`}
          />
          {field.helpText && <p className="text-xs text-muted-foreground">{field.helpText}</p>}
          {field.errorMessage && <p className="text-xs text-destructive">{field.errorMessage}</p>}
        </div>
      );

    case 'textarea':
      return (
        <div className="space-y-2">
          <Label>
            {field.label}
            {field.required && <span className="text-destructive ml-1">*</span>}
          </Label>
          <Textarea
            value={asStr}
            onChange={e => handleChange(e.target.value)}
            placeholder={field.placeholder}
            disabled={disabled}
            data-testid={`field-${field.key}`}
            rows={4}
          />
          {field.helpText && <p className="text-xs text-muted-foreground">{field.helpText}</p>}
          {field.errorMessage && <p className="text-xs text-destructive">{field.errorMessage}</p>}
        </div>
      );

    case 'number':
      return (
        <div className="space-y-2">
          <Label>
            {field.label}
            {field.required && <span className="text-destructive ml-1">*</span>}
          </Label>
          <Input
            type="number"
            value={asNum}
            onChange={e => handleChange(e.target.value ? Number(e.target.value) : undefined)}
            placeholder={field.placeholder}
            min={field.validation?.min}
            max={field.validation?.max}
            disabled={disabled}
            data-testid={`field-${field.key}`}
          />
          {field.helpText && <p className="text-xs text-muted-foreground">{field.helpText}</p>}
          {field.errorMessage && <p className="text-xs text-destructive">{field.errorMessage}</p>}
        </div>
      );

    case 'date': {
      const dateValue = asStr ? parse(asStr, 'yyyy-MM-dd', new Date()) : undefined;
      const isValidDate = dateValue && !isNaN(dateValue.getTime());
      return (
        <div className="space-y-2">
          <Label>
            {field.label}
            {field.required && <span className="text-destructive ml-1">*</span>}
          </Label>
          <Popover>
            <PopoverTrigger asChild>
              <Button
                variant="outline"
                disabled={disabled}
                data-testid={`field-${field.key}`}
                className={cn(
                  'w-full justify-start text-left font-normal',
                  !isValidDate && 'text-muted-foreground'
                )}
              >
                <CalendarIcon className="mr-2 h-4 w-4" />
                {isValidDate ? format(dateValue, 'dd MMM yyyy') : 'Select date...'}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0" align="start">
              <Calendar
                mode="single"
                selected={isValidDate ? dateValue : undefined}
                onSelect={date => handleChange(date ? format(date, 'yyyy-MM-dd') : '')}
                captionLayout="dropdown"
                startMonth={new Date(1940, 0)}
                endMonth={new Date(2040, 11)}
              />
            </PopoverContent>
          </Popover>
          {field.helpText && <p className="text-xs text-muted-foreground">{field.helpText}</p>}
          {field.errorMessage && <p className="text-xs text-destructive">{field.errorMessage}</p>}
        </div>
      );
    }

    case 'select':
      return (
        <div className="space-y-2">
          <Label>
            {field.label}
            {field.required && <span className="text-destructive ml-1">*</span>}
          </Label>
          <Select value={asStr} onValueChange={handleChange} disabled={disabled}>
            <SelectTrigger className="w-full" data-testid={`field-${field.key}`}>
              <SelectValue placeholder={field.placeholder || 'Select...'} />
            </SelectTrigger>
            <SelectContent>
              {resolvedOptions.length > 0
                ? resolvedOptions.map(opt => (
                    <SelectItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </SelectItem>
                  ))
                : field.options?.map(opt => (
                    <SelectItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </SelectItem>
                  ))}
            </SelectContent>
          </Select>
          {field.helpText && <p className="text-xs text-muted-foreground">{field.helpText}</p>}
          {field.errorMessage && <p className="text-xs text-destructive">{field.errorMessage}</p>}
        </div>
      );

    case 'multiSelect': {
      const toggleOption = (optValue: string) => {
        const current = asStrArray;
        const next = current.includes(optValue)
          ? current.filter(v => v !== optValue)
          : [...current, optValue];
        handleChange(next);
      };
      return (
        <div className="space-y-2">
          <Label>
            {field.label}
            {field.required && <span className="text-destructive ml-1">*</span>}
          </Label>
          <div className="flex flex-col gap-2" data-testid={`field-${field.key}`}>
            {resolvedOptions.map(opt => (
              <div key={opt.value} className="flex items-center gap-2">
                <Checkbox
                  id={`${field.key}-${opt.value}`}
                  checked={asStrArray.includes(opt.value)}
                  onCheckedChange={() => toggleOption(opt.value)}
                  disabled={disabled}
                />
                <Label htmlFor={`${field.key}-${opt.value}`} className="font-normal cursor-pointer">
                  {opt.label}
                </Label>
              </div>
            ))}
          </div>
          {field.helpText && <p className="text-xs text-muted-foreground">{field.helpText}</p>}
          {field.errorMessage && <p className="text-xs text-destructive">{field.errorMessage}</p>}
        </div>
      );
    }

    case 'boolean':
      return (
        <div className="space-y-2">
          <Label>
            {field.label}
            {field.required && <span className="text-destructive ml-1">*</span>}
          </Label>
          <RadioGroup
            value={value === true ? 'yes' : value === false ? 'no' : ''}
            onValueChange={v => handleChange(v === 'yes')}
            disabled={disabled}
            data-testid={`field-${field.key}`}
            className="flex gap-4"
          >
            <div className="flex items-center gap-2">
              <RadioGroupItem value="yes" id={`${field.key}-yes`} />
              <Label htmlFor={`${field.key}-yes`} className="font-normal">
                Yes
              </Label>
            </div>
            <div className="flex items-center gap-2">
              <RadioGroupItem value="no" id={`${field.key}-no`} />
              <Label htmlFor={`${field.key}-no`} className="font-normal">
                No
              </Label>
            </div>
          </RadioGroup>
        </div>
      );

    case 'checkbox':
      return (
        <div className="flex items-start gap-3 py-2">
          <Checkbox
            id={field.key}
            checked={asBool}
            onCheckedChange={checked => handleChange(checked === true)}
            data-testid={`field-${field.key}`}
            disabled={disabled}
          />
          <Label htmlFor={field.key} className="font-normal leading-relaxed cursor-pointer">
            {field.label}
            {field.required && <span className="text-destructive ml-1">*</span>}
          </Label>
        </div>
      );

    case 'address':
      return (
        <AddressField
          field={field}
          value={asAddress}
          onChange={val => handleChange(val)}
          disabled={disabled}
          showCountry={field.addressShowCountry}
        />
      );

    case 'repeatable_group':
      return (
        <RepeatableGroupField
          field={field}
          entries={asEntries}
          onChange={entries => handleChange(entries)}
          allValues={allValues}
          disabled={disabled}
        />
      );

    case 'conditional_detail':
      return (
        <ConditionalDetailField
          field={field}
          value={asConditional}
          onChange={val => handleChange(val)}
          disabled={disabled}
        />
      );

    case 'info':
      return (
        <div className="rounded-xl border border-border bg-muted/40 p-4">
          {field.label && (
            <p id={`info-label-${field.key}`} className="text-sm font-semibold text-foreground">
              {field.label}
            </p>
          )}
          {field.helpText && (
            <div
              tabIndex={0}
              role="region"
              aria-labelledby={field.label ? `info-label-${field.key}` : undefined}
              aria-label={field.label ? undefined : 'Notice'}
              className="mt-1 max-h-48 overflow-y-auto whitespace-pre-line rounded text-[13px] leading-relaxed text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
              {field.helpText}
            </div>
          )}
        </div>
      );

    default:
      return null;
  }
}
